import { EventEmitter, Injectable } from '@angular/core';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { SafariViewController } from '@ionic-native/safari-view-controller/ngx';
import { Platform } from '@ionic/angular';
import { LogService } from '../core/log.service';

export enum AppLaunchable {
  MAPFRE_SALUD,
  AFIN,
  MAPFRE
}
@Injectable({
  providedIn: 'root'
})
export class UrlHelper {
  public onOpen = new EventEmitter();
  private _isCallable = null;

  constructor(
    private safariViewController: SafariViewController,
    private iab: InAppBrowser,
    private platform: Platform,
    private log: LogService
  ) {}

  /** @description Checks if device is able to make phone calls */
  get isCallable() {
    if (this._isCallable === null) {
      this._isCallable =
        this.platform.is('mobile') ||
        this.platform.is('mobileweb') ||
        this.platform.is('ipad') ||
        this.platform.is('iphone');
    }

    return this._isCallable;
  }

  open(url: string) {
    if (!url || url === '') {
      return;
    }
    if (!this.platform.is('cordova')) {
      this.onOpen.emit();
      return Promise.resolve(this.iab.create(encodeURI(url), '_system'));
    }

    return this.safariViewController
      .isAvailable()
      .catch(() => false)
      .then(available => {
        if (available) {
          this.openSafariUrl(url);
        } else {
          this.onOpen.emit();
          this.iab.create(encodeURI(url), '_system');
        }
      }
      )
      .catch(error => {
        this.log.e(this, 'Not able to open inAppBrowser', error);
      });
  }

  openExternal(url: string | URL) {
    if (!url || url === '') {
      return;
    }
    const normalizedUrl = typeof url === 'string' ? encodeURI(url) : url.href;
    this.onOpen.emit();
    this.iab.create(normalizedUrl, '_system');
  }

  openPhone(phone: string | number) {
    if (!phone || !this.isCallable) {
      return;
    }
    this.iab.create(encodeURI(`tel:${phone}`), '_system');
  }

  openApp(app: AppLaunchable) {
    return this._openApp(this.getAppData(app));
  }

  isInstalled(app: AppLaunchable) {
    return this._isInstalled(this.getAppData(app));
  }

  openMarket(app: AppLaunchable) {
    if (this.platform.is('ios')) {
      return this.openIOSMarket(app);
    }

    return this.openAndroidMarket(app);
  }

  openIOSMarket(app: AppLaunchable) {
    const url = `https://itunes.apple.com/es/app/${this.getAppData(app).iosMarket}`;
    return this.iab.create(encodeURI(url), '_system');
  }

  openAndroidMarket(app: AppLaunchable) {
    const url = `https://play.google.com/store/apps/details?id=${
      this.getAppData(app).packageName
    }`;
    return this.iab.create(encodeURI(url), '_system');
  }

  private async _openApp({ uri, packageName }): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return Promise.resolve(false);
    }
    // Wrong parsing at cordova plugin, have to erase uri property on Android in order to work
    const params = this.platform.is('ios') ? { uri } : { packageName };

    return new Promise(resolve => {
      (window as any).plugins.launcher.launch({
        ...params,
        successCallback: () => resolve(true),
        errorCallback: error => {
          this.log.e(this, 'Error trying to open Mapfre Salud', error);
          resolve(false);
        }
      });
    });
  }

  private _isInstalled({ uri, packageName }): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return Promise.resolve(false);
    }
    const res = this.platform.is('ios') ? { uri } : { packageName };

    return new Promise(resolve => {
      (window as any).plugins.launcher.canLaunch({
        ...res,
        successCallback: () => resolve(true),
        errorCallback: () => resolve(false)
      });
    });
  }

  private openSafariUrl(url) {
    return this.safariViewController
      .show({
        url,
        hidden: false,
        animated: true,
        transition: 'curl',
        enterReaderModeIfAvailable: true,
        tintColor: '#d81e05'
      })
      .toPromise()
      .then(_ => this.onOpen.emit())
      .catch(error => {
        this.log.e(
          this,
          'Not able to open safari view controller, fallback to inAppBrowser',
          error
        );
        return this.iab.create(encodeURI(url), '_system');
      });
  }

  private getAppData(app: AppLaunchable) {
    switch (app) {
      case AppLaunchable.MAPFRE_SALUD:
        return {
          uri: 'tarjetadesalud://www.mapfre.com/salud/appointment',
          packageName: 'com.mapfre.tarjetadesalud',
          iosMarket: 'mapfre-salud/id1083924566'
        };
      case AppLaunchable.AFIN:
        return {
          uri: 'inversion://',
          packageName: 'com.mapfre.inversion',
          iosMarket: 'mapfre-afin/id1456285964'
        };
      case AppLaunchable.MAPFRE:
        return {
          uri: '',
          packageName: 'com.mapfre.mapfreapp',
          iosMarket: 'mapfre/id425349256'
        };
      default:
        break;
    }
  }

  redirectPost(url: string) {
    const sections = url?.split('?');
    const params = sections[1]?.split('&');
    const form = document.createElement('form');
    form.method = 'post';
    form.action = sections[0];
    form.target = '_blank';
    for (const param of params) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = param.split('=')?.[0];
      input.value = param.split('=')?.[1];
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }
}
